import {
  type CasGroupReportBody,
  type CasReportPdfParams,
  type CustomReport,
  type CustomReportCreateBody,
  type CustomReportListParams,
  type CustomReportShareToParentAppBody,
  type CustomReportShareToParentSmsBody,
  type CustomReportUnshareToParentAppBody,
  type CustomReportUpdateBody,
  type ExamGroupReportBody,
  type GroupReportFileParams,
  type LegacyReport,
  type LegacyReportBody,
  type LegacyReportCreateBody,
  type LegacyReportDuplicationParams,
  type RealTimeReport,
  type RealTimeReportListParams,
  type RealTimeReportMetricParams,
  type RealTimeReportSettingBody,
  type RealTimeReportSettingParams,
  type RealTimeReportSettingTarget,
  type RealTimeReportShareLinkBody,
  type RealTimeReportShareToParentAppBody,
  type RealTimeReportShareToParentSmsBody,
  type RecentReportParams,
  reportApi,
  type ReportCreatableStudentParams,
  type ReportDraftBody,
  type ReportFileParams,
  type ReportOpenBody,
  type ReportScoringHistoryParams,
  type ReportsFileParams,
  type WorksheetGroupReportBody,
  type WorksheetReportPdfParams,
} from '@mathflat/domain/Report'
import type { Revision } from '@mathflat/domain/SchoolSystem'
import parse from 'date-fns/parse'
import qs from 'qs'

import type { Student } from '@/dto'
import { CommonError, Report } from '@/dto'
import { SimpleReport } from '@/dto/Report/SimpleReport'
import { CheckableColl } from '@/models/CheckableColl'
import CheckableMap from '@/models/CheckableMap'
import type { YearMonthStr } from '@/types'
import { createLatestRequestSignal } from '@/utils/axios/latestRequestSignal'
import { setHeaderType } from '@/utils/download'

import { ReportModelV2 } from '../page/Lesson/Report/_model/report.model'
import type Controller from './Controller'

export const NOT_AVAILABLE_ERROR = 'NOT_AVAILABLE'

const getCreatableStudentIds = (params: ReportCreatableStudentParams) =>
  reportApi.getReportCreatableStudentIds(params, {
    mp_event_name: 'T44-05_수업보고서생성가능학생조회_API',
  })

// 인터셉터의 빈 문자열 → null 변환(emptyStringToNull)이 두 단계 이상 깊은 객체에는 닿지 않아 option.comment 만 직접 바꾼다
const withNullComment = (body: LegacyReportCreateBody): LegacyReportCreateBody => ({
  ...body,
  option: { ...body.option, comment: body.option.comment === '' ? null : body.option.comment },
})

class ReportController implements Controller {
  private signalFor = createLatestRequestSignal('report/recently')

  adapter = {
    reportV2: {
      from(data: RealTimeReport | CustomReport) {
        return new ReportModelV2(data)
      },
    },
    simpleReport: {
      from(data: LegacyReport): SimpleReport {
        return new SimpleReport(data)
      },
      to({ targetDate }) {
        return qs.stringify(
          {
            targetDate,
          },
          { arrayFormat: 'comma' },
        )
      },
    },
  }

  async fetchV2ReportRealTime(params: RealTimeReportListParams) {
    const reports = await reportApi.getRealTimeReports(params)
    return reports.map(this.adapter.reportV2.from)
  }

  fetchV2ReportRealTimeMetric(params: RealTimeReportMetricParams) {
    return reportApi.getRealTimeReportMetric(params)
  }

  fetchV2ReportRealTimeSetting(params: RealTimeReportSettingParams) {
    return reportApi.getRealTimeReportSetting(params)
  }

  async updateV2ReportRealTimeSetting(
    target: RealTimeReportSettingTarget,
    body: RealTimeReportSettingBody,
  ) {
    await reportApi.updateRealTimeReportSetting(target, body)
  }

  async fetchV2ReportCustom(params: CustomReportListParams) {
    const { content, last } = await reportApi.getCustomReports(params)
    return {
      content: content.map(this.adapter.reportV2.from),
      isLast: last,
    }
  }

  fetchV2ReportCustomScoringHistoryCount(params: ReportScoringHistoryParams) {
    return reportApi.getReportScoringHistoryCount(params)
  }

  fetchV2ReportCustomScoringHistory(params: ReportScoringHistoryParams) {
    return reportApi.getReportScoringHistory(params)
  }

  fetchV2ReportCustomScoring(body: ReportDraftBody, signal?: AbortSignal) {
    return reportApi.createReportDraft(body, { signal })
  }

  fetchV2ReportCustomReportIdScoring({ reportId }: { reportId: CustomReport['id'] }) {
    return reportApi.getCustomReportEditSource(reportId)
  }

  async createV2ReportCustom(body: CustomReportCreateBody) {
    await reportApi.createCustomReports(body)
  }

  async updateV2ReportCustom({
    reportId,
    ...body
  }: CustomReportUpdateBody & { reportId: CustomReport['id'] }) {
    await reportApi.updateCustomReport(reportId, body)
  }

  async shareV2ReportCustomToParentApp(body: CustomReportShareToParentAppBody) {
    await reportApi.shareCustomReportsToParentApp(body)
  }

  async unshareV2ReportCustomToParentApp(body: CustomReportUnshareToParentAppBody) {
    await reportApi.unshareCustomReportsToParentApp(body)
  }

  async shareV2ReportCustomToParentSms(body: CustomReportShareToParentSmsBody) {
    await reportApi.shareCustomReportsToParentSms(body)
  }

  async shareReportRealTimeToParentApp(body: RealTimeReportShareToParentAppBody) {
    await reportApi.shareRealTimeReportToParentApp(body)
  }

  async shareReportRealTimeToParentSms(body: RealTimeReportShareToParentSmsBody) {
    await reportApi.shareRealTimeReportToParentSms(body)
  }

  shareV2ReportCustomByLink({ reportId }: { reportId: CustomReport['id'] }) {
    return reportApi.createCustomReportShareLink(reportId)
  }

  shareReportRealTimeByLink(body: RealTimeReportShareLinkBody) {
    return reportApi.createRealTimeReportShareLink(body)
  }

  downloadV2ReportRealTime(params: ReportFileParams) {
    return reportApi.getRealTimeReportFile(params)
  }

  async fetchShowReportToParents(body: ReportOpenBody) {
    const { reportIds, opened } = body
    const eventName = (() => {
      if (reportIds.length > 1) {
        return opened
          ? 'T44-17_보고서목록_학부모앱_일괄공개전환_API'
          : 'T44-18_보고서목록_학부모앱_일괄비공개전환_API'
      } else {
        return opened
          ? 'T44-15_보고서목록_학부모앱_공개전환_API'
          : 'T44-16_보고서목록_학부모앱_비공개전환_API'
      }
    })()
    await reportApi.updateLegacyReportOpen(body, { mp_event_name: eventName })
  }

  async fetchNavigationRange(params: RecentReportParams) {
    const { year, month } = await reportApi.getRecentReportYearMonth(params, {
      meta: { mp_event_name: 'T44-14_최근생성된수업보고서_년월조회_API' },
      signal: this.signalFor({ useAbortController: true }),
    })
    return parse(`${year}-${month}`, 'yyyy-MM', new Date())
  }

  async fetchReportsById({ reportIds }: { reportIds: LegacyReport['id'][] }) {
    const reports = await reportApi.getLegacyReports(reportIds)
    const parsedData = reports.map(this.adapter.simpleReport.from)
    const reportMap = CheckableMap.fromArr(parsedData, 'id')
    return new CheckableColl<SimpleReport, 'id'>(reportMap)
  }

  async fetchCustomReportsV2ById({ ids }: { ids: CustomReport['id'][] }) {
    const reports = await reportApi.getCustomReportsByIds(ids)
    return reports.map(this.adapter.reportV2.from)
  }

  async fetchStudentReport({
    studentId,
    reportId,
  }: {
    studentId: Student['id']
    reportId: Report['id']
  }) {
    const report = await reportApi.getLegacyReport(studentId, reportId, {
      mp_event_name: 'T44-08_수업보고서조회_API',
    })
    return new Report(report)
  }

  async fetchMonthlyAvailableReports(studentId: Student['id'], revision: Revision) {
    const availabilities = await reportApi.getMonthlyReportAvailabilities(studentId, revision, {
      mp_event_name: 'T44-08_월별수업보고서생성시생성가능한월조회_API',
    })
    // 서버 YearMonth 는 yyyy-MM 로 오고 스키마가 경계에서 검증한다
    return availabilities.reverse().map((availability) => ({
      ...availability,
      yearMonth: availability.yearMonth as YearMonthStr<'-'>,
    }))
  }

  async download(params: ReportsFileParams) {
    return reportApi.getReportsFile(params, { mp_event_name: 'T44-09_수업보고서다운로드_API' })
  }

  async downloadOneClick(params: WorksheetReportPdfParams) {
    return setHeaderType(
      await reportApi.getWorksheetReportPdf(params, {
        mp_event_name: 'T42-10_원클릭보고서_생성버튼선택_API',
      }),
    )
  }

  async downloadOneClickGroup(params: GroupReportFileParams) {
    return reportApi.getWorksheetGroupReportFile(params, {
      mp_event_name: 'T61-01_그룹원클릭보고서_다운로드_API',
    })
  }

  async requestCreateOneClickGroupReport(body: WorksheetGroupReportBody) {
    await reportApi.createWorksheetGroupReport(body, {
      mp_event_name: 'T42-03_그룹원클릭보고서_생성선택_API',
    })
  }

  async downloadCasOneClick(params: CasReportPdfParams) {
    return setHeaderType(await reportApi.getCasReportPdf(params))
  }

  async downloadCasOneClickGroup(params: GroupReportFileParams) {
    return reportApi.getCasGroupReportFile(params)
  }

  async requestCreateCasOneClickGroupReport(body: CasGroupReportBody) {
    await reportApi.createCasGroupReport(body)
  }

  async requestCreateGroupExamReport(body: ExamGroupReportBody) {
    await reportApi.createExamGroupReport(body)
  }

  async downloadGroupExamReport(params: GroupReportFileParams) {
    return reportApi.getExamGroupReportFile(params)
  }

  async checkDuplicationReport(params: LegacyReportDuplicationParams) {
    return reportApi.checkLegacyReportDuplication(params, {
      mp_event_name: 'T44-01_수업보고서중복체크_API',
    })
  }

  async createStudentReport(body: LegacyReportCreateBody) {
    return reportApi.createLegacyReports(withNullComment(body), {
      mp_event_name: 'T44-07_수업보고서생성_API',
    })
  }

  async createGroupReport(body: LegacyReportCreateBody) {
    return reportApi.createLegacyReports(withNullComment(body), {
      mp_event_name: 'T44-06_그룹수업보고서생성_API',
    })
  }

  async updateStudentReport({
    studentId,
    reportId,
    report,
  }: {
    studentId: Student['id']
    reportId: Report['id']
    report: LegacyReportBody
  }) {
    return reportApi.updateLegacyReport(studentId, reportId, report, {
      mp_event_name: 'T44-08_수업보고서수정_API',
    })
  }

  async deleteReports(reportIds: ReportModelV2['id'][]) {
    await reportApi.deleteLegacyReports(reportIds, {
      mp_event_name: 'T44-13_수업보고서일괄삭제_API',
    })
  }

  async checkValidStudentReport(params: ReportCreatableStudentParams) {
    const data = await getCreatableStudentIds(params)
    if (data.length === 1) {
      return true
    } else {
      throw new CommonError(NOT_AVAILABLE_ERROR)
    }
  }

  async fetchAvailableGroupReportStudentIds(params: ReportCreatableStudentParams) {
    const data = await getCreatableStudentIds(params)
    if (data.length > 0) {
      return data
    } else {
      throw new CommonError(NOT_AVAILABLE_ERROR)
    }
  }
}

export const reportController = new ReportController()
